import { useSession } from 'next-auth/react';
import { useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { NEXT_AUTH_STATUSES, SHARED_ITEM_TYPE } from '@/lib/constants';
import { SharedItemType } from '@/lib/models/shared-item/types';
import { SharedItemApiResponse } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { getSharedItemsFindUrl, getSharedItemsUrl } from '@/utils/helpers/api-urls';
import { logEvent } from '@/utils/logger';

interface UseShareShipmentProps {
  itemId: string;
  isEnabled?: boolean;
  type?: SharedItemType;
  shouldCreate?: boolean;
}

const ERROR_MESSAGE = 'Error requesting shared item';

export const useShareItem = ({
  itemId,
  isEnabled: isEnabledProp,
  type = SHARED_ITEM_TYPE.SHIPMENT,
  shouldCreate = true,
}: UseShareShipmentProps) => {
  const { status } = useSession();
  const [error, setError] = useState<string | null>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const isEnabled = status === NEXT_AUTH_STATUSES.AUTHENTICATED && isEnabledProp;
  const path = `${getSharedItemsFindUrl()}?${new URLSearchParams({ sharedId: itemId, type, shouldCreate: shouldCreate.toString() })}`;

  const {
    data,
    error: swrError,
    isLoading: swrIsLoading,
    mutate,
  } = useSWRImmutable<SharedItemApiResponse>(isEnabled ? path : null, request, { keepPreviousData: true });

  const createSharedItem = async () => {
    if (data?.sharedItem) return null;
    setIsLoading(true);
    setError(undefined);

    try {
      const result = await request<SharedItemApiResponse>(path, undefined, 'POST');

      if (result.error) {
        return setError(result.error);
      }

      return await mutate({ ...data, sharedItem: result.sharedItem });
    } catch (e: any) {
      setError(e.message);
      logEvent.error('Error creating shared item', e);
    } finally {
      setIsLoading(false);
    }

    return null;
  };

  const updateSharedItem = async (change: { orgId?: string; isEnabled?: boolean }) => {
    if (!data?.sharedItem) return null;
    setIsLoading(true);
    setError(undefined);

    try {
      const result = await request<SharedItemApiResponse>(
        getSharedItemsUrl(data.sharedItem.id),
        { body: { ...data.sharedItem, ...change } },
        'POST',
      );

      if (result.error) {
        return setError(result.error);
      }

      return await mutate({ ...data, sharedItem: result.sharedItem });
    } catch (e: any) {
      setError(e.message);
      logEvent.error('Error updating shared item', e);
    } finally {
      setIsLoading(false);
    }

    return null;
  };

  const errorMessage = error || swrError ? ERROR_MESSAGE : undefined;

  return {
    isLoading: swrIsLoading || (!data?.sharedItem && isLoading),
    sharedItem: data?.sharedItem,
    error: errorMessage,
    updateSharedItem,
    createSharedItem,
  };
};
